import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { prepareForUpload } from '@/features/messages/attachments';
import { newId } from '@/lib/id';

/**
 * בקשות: חופשה, מחלה ובקשה כללית (PRD 5.10.3).
 *
 * ההכרעה עוברת ב RPC ולא בעדכון ישיר, כי אישור חופשה גם רושם אילוץ
 * בסידור. שתי הפעולות חייבות לקרות יחד, אחרת מנהל ישבץ עובד שכבר
 * אישרו לו חופשה.
 */

export type RequestType = {
  id: string;
  name: string;
  requires_attachment: boolean;
  is_sensitive: boolean;
  blocks_schedule: boolean;
};

export type HrRequest = {
  id: string;
  employee_id: string;
  type_id: string;
  from_date: string | null;
  to_date: string | null;
  note: string | null;
  attachment_path: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  decided_at: string | null;
  created_at: string;
  hr_request_types: { name: string; is_sensitive: boolean } | null;
  employees: { first_name: string; last_name: string } | null;
};

const SELECT = `
  id, employee_id, type_id, from_date, to_date, note, attachment_path, status,
  decided_at, created_at,
  hr_request_types ( name, is_sensitive ),
  employees!hr_requests_employee_id_fkey ( first_name, last_name )
`;

export const REQUESTS_KEY = ['requests'] as const;

/** ה RLS מחזיר לעובד את שלו, ולמנהל עם hr.manage את כולם. */
export function useRequests() {
  return useQuery({
    queryKey: REQUESTS_KEY,
    queryFn: async (): Promise<HrRequest[]> => {
      const { data, error } = await supabase
        .from('hr_requests')
        .select(SELECT)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as HrRequest[];
    },
  });
}

/**
 * הבקשות של עובד אחד, לכרטיס שלו במסך העובדים.
 *
 * שאילתה משלה ולא סינון של הרשימה המלאה: מסך העובדים הוא המסך
 * הנפוץ ביותר בקונסולה, ומשיכת כל טבלת הבקשות בכל כניסה אליו כדי
 * להראות שלוש שורות בכרטיס אחד שנפתח היא מחיר שמשלמים תמיד עבור
 * משהו שקורה לפעמים. נטענת רק כשהכרטיס פתוח.
 */
export function useEmployeeRequests(employeeId: string | null, enabled = true) {
  return useQuery({
    queryKey: [...REQUESTS_KEY, 'employee', employeeId],
    enabled: Boolean(employeeId) && enabled,
    queryFn: async (): Promise<HrRequest[]> => {
      const { data, error } = await supabase
        .from('hr_requests')
        .select(SELECT)
        .eq('employee_id', employeeId as string)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as HrRequest[];
    },
  });
}

/** כמה ממתינות, לתג שעל הכפתור במסך העובדים. */
export function usePendingRequestsCount() {
  return useQuery({
    queryKey: [...REQUESTS_KEY, 'pending-count'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('hr_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useRequestTypes() {
  return useQuery({
    queryKey: [...REQUESTS_KEY, 'types'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<RequestType[]> => {
      const { data, error } = await supabase
        .from('hr_request_types')
        .select('id, name, requires_attachment, is_sensitive, blocks_schedule')
        .order('name');
      if (error) throw error;
      return (data ?? []) as RequestType[];
    },
  });
}

export function useRequestMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    void queryClient.invalidateQueries({ queryKey: ['constraints'] });
  };

  const create = useMutation({
    mutationFn: async (input: {
      employee_id: string;
      type_id: string;
      from_date: string | null;
      to_date: string | null;
      note: string | null;
      attachment_path: string | null;
    }) => {
      const { error } = await supabase.from('hr_requests').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const decide = useMutation({
    mutationFn: async (input: { id: string; approve: boolean; note?: string }) => {
      const { error } = await supabase.rpc('decide_hr_request', {
        p_request: input.id,
        p_approve: input.approve,
        p_note: input.note ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('cancel_hr_request', { p_request: id });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { create, decide, cancel };
}

/** אישור מחלה או קובץ אחר. הנתיב מתחיל במזהה העובד. */
export async function uploadAttachment(employeeId: string, file: File): Promise<string> {
  const prepared = await prepareForUpload(file);
  const ext =
    prepared.type === 'application/pdf'
      ? 'pdf'
      : (prepared.type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'jpg');
  const path = `${employeeId}/${newId()}.${ext}`;

  const { error } = await supabase.storage.from('requests').upload(path, prepared, {
    contentType: prepared.type || 'application/octet-stream',
    upsert: false,
  });
  if (error) throw error;
  return path;
}

export function useAttachmentUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ['request-attachment', path],
    enabled: Boolean(path),
    staleTime: 50 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.storage
        .from('requests')
        .createSignedUrl(path as string, 3600);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

export const requestStatusLabel: Record<HrRequest['status'], string> = {
  pending: 'ממתינה',
  approved: 'אושרה',
  rejected: 'נדחתה',
  cancelled: 'בוטלה',
};

export const requestStatusTone: Record<HrRequest['status'], 'good' | 'warn' | 'bad'> = {
  pending: 'warn',
  approved: 'good',
  rejected: 'bad',
  cancelled: 'bad',
};

/** "12.03 עד 14.03", או תאריך אחד כשאין טווח. */
export function rangeLabel(from: string | null, to: string | null): string {
  if (!from) return '';
  const short = (value: string) => {
    const [, month, day] = value.split('-');
    return `${day}.${month}`;
  };
  if (!to || to === from) return short(from);
  return `${short(from)} עד ${short(to)}`;
}
